import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { 
  UserPlus, CheckCircle, XCircle, Eye, Loader2, Clock, 
  Building2, User, Mail, Phone, MapPin
} from 'lucide-react';
import { toast } from 'sonner';

const SolicitacoesPage = () => {
  const { api } = useAuth();
  const [solicitacoes, setSolicitacoes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSolicitacao, setSelectedSolicitacao] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [motivoRejeicao, setMotivoRejeicao] = useState('');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchSolicitacoes();
  }, []);

  const fetchSolicitacoes = async () => {
    try {
      const response = await api.get('/solicitacoes-acesso');
      setSolicitacoes(response.data);
    } catch (error) {
      console.error('Error fetching solicitacoes:', error);
      toast.error('Erro ao carregar solicitações');
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (solicitacao) => {
    setSelectedSolicitacao(solicitacao);
    setDialogOpen(true);
  };

  const handleAprovar = async (solicitacaoId) => {
    setProcessing(true);
    try {
      await api.post(`/solicitacoes-acesso/${solicitacaoId}/aprovar`);
      toast.success('Solicitação aprovada! Empresa e usuário criados.');
      setDialogOpen(false);
      fetchSolicitacoes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao aprovar solicitação');
    } finally {
      setProcessing(false);
    }
  };

  const handleRejeitar = async () => {
    if (!selectedSolicitacao) return;
    
    setProcessing(true);
    try {
      await api.post(`/solicitacoes-acesso/${selectedSolicitacao.id}/rejeitar?motivo=${encodeURIComponent(motivoRejeicao)}`);
      toast.success('Solicitação rejeitada');
      setRejectDialogOpen(false);
      setDialogOpen(false);
      setMotivoRejeicao('');
      fetchSolicitacoes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao rejeitar solicitação');
    } finally {
      setProcessing(false);
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      pendente: { label: 'Pendente', className: 'bg-warning', icon: Clock },
      aprovada: { label: 'Aprovada', className: 'bg-accent', icon: CheckCircle },
      rejeitada: { label: 'Rejeitada', className: 'bg-destructive', icon: XCircle }
    };
    const c = config[status] || config.pendente;
    return (
      <Badge className={c.className}>
        <c.icon className="w-3 h-3 mr-1" />
        {c.label}
      </Badge>
    );
  };

  const pendentes = solicitacoes.filter(s => s.status === 'pendente');

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-accent" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="solicitacoes-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-primary font-['Outfit'] mb-1">Solicitações de Acesso</h2>
          <p className="text-muted-foreground">Aprove ou rejeite solicitações de novas empresas</p>
        </div>
        {pendentes.length > 0 && (
          <Badge className="bg-warning text-white">
            {pendentes.length} pendente(s)
          </Badge>
        )}
      </div>

      <Card className="border-slate-100">
        <CardContent className="pt-6">
          {solicitacoes.length === 0 ? (
            <div className="text-center py-12">
              <UserPlus className="w-12 h-12 mx-auto mb-3 text-muted-foreground opacity-50" />
              <p className="text-muted-foreground">Nenhuma solicitação de acesso</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Empresa</TableHead>
                    <TableHead>CNPJ</TableHead>
                    <TableHead>Solicitante</TableHead>
                    <TableHead>Data</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {solicitacoes.map((solicitacao) => (
                    <TableRow key={solicitacao.id} data-testid={`solicitacao-row-${solicitacao.id}`}>
                      <TableCell>
                        <div>
                          <p className="font-medium">{solicitacao.empresa?.razao_social}</p>
                          <p className="text-sm text-muted-foreground">{solicitacao.empresa?.nome_fantasia}</p>
                        </div>
                      </TableCell>
                      <TableCell className="font-mono text-sm">{solicitacao.empresa?.cnpj}</TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">{solicitacao.usuario?.nome}</p>
                          <p className="text-sm text-muted-foreground">{solicitacao.usuario?.email}</p>
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {new Date(solicitacao.created_at).toLocaleDateString('pt-BR')}
                      </TableCell>
                      <TableCell>{getStatusBadge(solicitacao.status)}</TableCell>
                      <TableCell>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => handleViewDetails(solicitacao)}
                          data-testid={`view-${solicitacao.id}`}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          Ver
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-['Outfit']">Detalhes da Solicitação</DialogTitle>
            <DialogDescription>
              Revise os dados antes de aprovar ou rejeitar
            </DialogDescription>
          </DialogHeader>
          
          {selectedSolicitacao && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                {getStatusBadge(selectedSolicitacao.status)}
                <span className="text-sm text-muted-foreground">
                  {new Date(selectedSolicitacao.created_at).toLocaleString('pt-BR')}
                </span>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                {/* Empresa Info */}
                <div className="space-y-4">
                  <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                    <Building2 className="w-4 h-4" />
                    Dados da Empresa
                  </div>
                  <div className="bg-slate-50 rounded-lg p-4 space-y-3">
                    <div>
                      <p className="text-sm text-muted-foreground">Razão Social</p>
                      <p className="font-medium">{selectedSolicitacao.empresa?.razao_social}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Nome Fantasia</p>
                      <p className="font-medium">{selectedSolicitacao.empresa?.nome_fantasia}</p>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <p className="text-sm text-muted-foreground">CNPJ</p>
                        <p className="font-mono text-sm">{selectedSolicitacao.empresa?.cnpj}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">CNAE</p>
                        <p className="font-mono text-sm">{selectedSolicitacao.empresa?.cnae}</p>
                      </div>
                    </div>
                    {selectedSolicitacao.empresa?.email && (
                      <div className="flex items-center gap-2">
                        <Mail className="w-4 h-4 text-muted-foreground" />
                        <span className="text-sm">{selectedSolicitacao.empresa?.email}</span>
                      </div>
                    )}
                    {selectedSolicitacao.empresa?.telefone && (
                      <div className="flex items-center gap-2">
                        <Phone className="w-4 h-4 text-muted-foreground" />
                        <span className="text-sm">{selectedSolicitacao.empresa?.telefone}</span>
                      </div>
                    )}
                    {selectedSolicitacao.empresa?.endereco && (
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-muted-foreground" />
                        <span className="text-sm">{selectedSolicitacao.empresa?.endereco}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Usuario Info */}
                <div className="space-y-4">
                  <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                    <User className="w-4 h-4" />
                    Dados do Administrador
                  </div>
                  <div className="bg-slate-50 rounded-lg p-4 space-y-3">
                    <div>
                      <p className="text-sm text-muted-foreground">Nome</p>
                      <p className="font-medium">{selectedSolicitacao.usuario?.nome}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Mail className="w-4 h-4 text-muted-foreground" />
                      <span className="text-sm">{selectedSolicitacao.usuario?.email}</span>
                    </div>
                    {selectedSolicitacao.usuario?.telefone && (
                      <div className="flex items-center gap-2">
                        <Phone className="w-4 h-4 text-muted-foreground" />
                        <span className="text-sm">{selectedSolicitacao.usuario?.telefone}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {selectedSolicitacao.motivo_rejeicao && (
                <div className="bg-destructive/10 rounded-lg p-4">
                  <p className="text-sm font-medium text-destructive mb-1">Motivo da Rejeição:</p>
                  <p className="text-sm">{selectedSolicitacao.motivo_rejeicao}</p>
                </div>
              )}

              {selectedSolicitacao.status === 'pendente' && (
                <div className="flex justify-end gap-3 pt-4 border-t">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setRejectDialogOpen(true);
                    }}
                    disabled={processing}
                    className="text-destructive"
                    data-testid="btn-rejeitar"
                  >
                    <XCircle className="w-4 h-4 mr-2" />
                    Rejeitar
                  </Button>
                  <Button
                    onClick={() => handleAprovar(selectedSolicitacao.id)}
                    disabled={processing}
                    className="bg-accent hover:bg-accent/90"
                    data-testid="btn-aprovar"
                  >
                    {processing ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <CheckCircle className="w-4 h-4 mr-2" />
                    )}
                    Aprovar
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-['Outfit']">Rejeitar Solicitação</DialogTitle>
            <DialogDescription>
              Informe o motivo da rejeição (opcional)
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="motivo">Motivo da Rejeição</Label>
              <Textarea
                id="motivo"
                value={motivoRejeicao}
                onChange={(e) => setMotivoRejeicao(e.target.value)}
                placeholder="Descreva o motivo da rejeição..."
                rows={3}
              />
            </div>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setRejectDialogOpen(false)}>
                Cancelar
              </Button>
              <Button
                onClick={handleRejeitar}
                disabled={processing}
                className="bg-destructive hover:bg-destructive/90"
              >
                {processing ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <XCircle className="w-4 h-4 mr-2" />
                )}
                Confirmar Rejeição
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SolicitacoesPage;
